/**
 * Filter options for General Viewer (and other filter UIs): groups, views, quality flags.
 * Fetched from backend config API (GET /config/views, GET /config/categories).
 * Does not use frontend planes.config; does not extend CategoriesContext so other consumers are unchanged.
 */
import React, { createContext, useMemo, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchViews, fetchCategories } from '../helpers/api/config/config';
import type { ViewDefResponse } from '../helpers/api/config/config';
import type { CategoryDef } from '../helpers/api/config/config';
import { useProfile } from '../helpers/api/auth/auth';
import { isSharedReportPath, isSignupPath } from '../publicPaths';

/** Normalize known category_group typos / synonyms from config (e.g. abdomen → abdominal). */
export function canonicalizeCategoryGroup(group: string | null | undefined): string {
  const raw = String(group ?? '').trim();
  if (!raw) return '';
  const key = raw.toLowerCase().replace(/\s+/g, '_');
  const aliases: Record<string, string> = {
    abdomen: 'abdominal',
    abdominals: 'abdominal',
  };
  return aliases[key] ?? key;
}

export interface FilterOptionsContextValue {
  /** Unique category_group values from model_category_def (GET /config/categories) */
  groups: string[];
  /** Views from GET /config/views */
  views: ViewDefResponse[];
  /** Quality flags from categories: { id, code, name } for filter dropdowns */
  qualityFlags: Array<{ id: number; code: string; name: string }>;
  /** category_id -> category_group for filtering views by selected groups */
  categoryIdToGroup: Record<number, string>;
  isLoading: boolean;
  isError: boolean;
}

const FilterOptionsContext = createContext<FilterOptionsContextValue | null>(null);

const VIEWS_QUERY_KEY = ['config', 'views'] as const;
const CATEGORIES_QUERY_KEY = ['config', 'categories'] as const;

export function FilterOptionsProvider({ children }: { children: React.ReactNode }) {
  // Same reason as CategoriesProvider: this mounts above the router, and the
  // patient share page has no session to ask about.
  const me = useProfile({
    retry: 0,
    enabled: !isSharedReportPath(window.location.pathname) && !isSignupPath(window.location.pathname),
  });
  const isAuthenticated = !!me?.data;

  const { data: views = [], isLoading: viewsLoading, isError: viewsError } = useQuery({
    queryKey: VIEWS_QUERY_KEY,
    queryFn: fetchViews,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 2,
  });

  const { data: categories = [], isLoading: categoriesLoading, isError: categoriesError } = useQuery({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: fetchCategories,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 2,
  });

  const value = useMemo<FilterOptionsContextValue>(() => {
    const cats = categories as CategoryDef[];
    const groups = Array.from(
      new Set(
        cats
          .map((c) => canonicalizeCategoryGroup(c.category_group))
          .filter((g): g is string => !!g)
      )
    ).sort();
    const qualityFlags = cats.map((c) => ({
      id: c.id,
      code: c.code,
      name: c.display_name || c.code,
    }));
    const categoryIdToGroup: Record<number, string> = {};
    cats.forEach((c) => {
      const g = canonicalizeCategoryGroup(c.category_group);
      if (g) categoryIdToGroup[c.id] = g;
    });
    return {
      groups,
      views: views as ViewDefResponse[],
      qualityFlags,
      categoryIdToGroup,
      isLoading: viewsLoading || categoriesLoading,
      isError: viewsError || categoriesError,
    };
  }, [views, categories, viewsLoading, categoriesLoading, viewsError, categoriesError]);

  return (
    <FilterOptionsContext.Provider value={value}>
      {children}
    </FilterOptionsContext.Provider>
  );
}

export function useFilterOptions(): FilterOptionsContextValue {
  const ctx = useContext(FilterOptionsContext);
  if (!ctx) {
    throw new Error('useFilterOptions must be used within FilterOptionsProvider');
  }
  return ctx;
}

export function useFilterOptionsOptional(): FilterOptionsContextValue | null {
  return useContext(FilterOptionsContext);
}
